import { ifAll, ifAny, IFieldDefFn, IFieldSettings, IGetValueArgs, not } from "@components/smart/FieldInfo";
import { ActionButtonPosition } from "@components/smart/smartFastEntryList";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IFormGroupDef } from "@components/smart/smartFormGroup/SmartFormGroup";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import Text from "@components/text";
import {
    EntitySetName,
    EntityTypeName,
    IPrEmploymentEntity,
    IPrEmploymentTemplateEntity,
    IPrSalaryComponentEntity,
    IPrWorkingPatternEntity,
    PrBenefitEntity,
    PrEmploymentBenefitEntity,
    PrEmploymentCarEntity,
    PrEmploymentEntity,
    PrEmploymentExtraSalaryEntity,
    PrEmploymentExtraSalaryTemporalCurrentEntity,
    PrEmploymentRetirementSavingEntity,
    PrEmploymentRetirementSavingTemporalCurrentEntity,
    PrEmploymentRetirementSavingTemporalEntity,
    PrEmploymentSalaryComponentEntity,
    PrEmploymentSalaryComponentTemporalCurrentEntity,
    PrEmploymentTemplateBenefitEntity,
    PrEmploymentTemplateEntity,
    PrEmploymentTemplateExtraSalaryEntity,
    PrEmploymentTemplateExtraSalaryTemporalCurrentEntity,
    PrEmploymentTemplateSalaryComponentEntity,
    PrEmploymentTemplateTemporalEntity,
    PrEmploymentTemporalEntity,
    PrExtraSalaryEntity,
    PrExtraSalaryTypeEntity,
    PrRetirementSavingsCompanyEntity,
    PrRetirementSavingsProductEntity,
    PrSalaryComponentEntity,
    PrWorkingPatternDayEntity,
    PrWorkingPatternEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrEmploymentTypeCode,
    PrRegularSalaryTypeCode,
    PrSalaryComponentTypeCode,
    PrWorkingPatternTypeCode
} from "@odata/GeneratedEnums";
import { IFormatOptions } from "@odata/OData.utils";
import {
    getTemporalAdditionalProperties,
    getTemporalDataColumns,
    getTemporalPropertyExtraFieldContentAfter,
    getTemporalPropertyFieldDefinition,
    TemporalEntityProp,
    TemporalValidityFieldsDef
} from "@odata/TemporalUtils";
import {
    areAllBenefitsOfSameType,
    BenefitVariant,
    getBenefitColumns,
    getBenefitFieldDefinitions,
    getBenefitTemporalDialogSettings
} from "@pages/payroll/benefits/Benefit.utils";
import { IEmploymentCustomData } from "@pages/payroll/employment/EmploymentFormView";
import {
    ExtraSalaryVariant,
    getExtraSalaryFieldDefinitions,
    getExtraSalaryTemporalDialogSettings
} from "@pages/payroll/extraSalary/ExtraSalary.utils";
import { getPayrollCurrencyFormatter, getPayrollCurrencyUnit } from "@pages/payroll/payroll.utils";
import { getLeaveDaysDef, getSickDaysDef } from "@pages/payroll/payrollSetting/PayrollSettingDef";
import { productPath } from "@pages/payroll/payslip/Payslip.utils";
import { areAllRetirementsOfSameType } from "@pages/payroll/retirementSavingsCompany/RetirementSavings.utils";
import {
    areAllSalaryComponentsOfSameType,
    getSalaryComponentColumns,
    getSalaryComponentFieldDefinitions,
    getSalaryComponentTemporalDialogSettings,
    SalaryComponentVariant
} from "@pages/payroll/salaryComponent/SalaryComponent.utils";
import {
    DEFAULT_FULL_TIME_HOURS_PER_WEEK,
    getWeeklyWorkingHours
} from "@pages/payroll/workingPatterns/WorkingPatterns.utils";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import { isObjectEmpty, roundToDecimalPlaces } from "@utils/general";
import i18next from "i18next";
import React from "react";

import { BasicInputSizes, FieldType, LabelStatus, NavigationSource, Sort, TextAlign } from "../../../enums";
import { TValue } from "../../../global.types";
import { Model } from "../../../model/Model";
import BindingContext, { createPath } from "../../../odata/BindingContext";
import { formatCurrency } from "../../../types/Currency";
import { IFormDef } from "../../../views/formView/Form";
import { isFormDisabled, isFormReadOnly } from "../../../views/formView/Form.utils";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import {
    getNumberOursSummaryDef,
    getNumberRangeFieldDefs,
    NumberRangeAdditionalProperties
} from "../../numberRange/NumberRange.utils";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition, TFieldsDefinition } from "../../PageUtils";
import {
    BenefitGrpId,
    DialogType,
    EmploymentTranslationFiles,
    ExtraSalaryGrpId,
    ExtraSalaryTogglePath,
    getExtractHistoryFromSettingsFn,
    isLegislativeExtraSalary,
    isLegislativeExtraSalaryItem,
    LegislativeExtraSalaryLocalPath,
    SalaryComponentGrpId
} from "../employment/Employment.utils";
import CollapsibleSectionToggleField from "../extraSalary/CollapsibleSectionToggleField";
import LegislativeExtraSalariesSmartFastEntryList from "../extraSalary/LegislativeExtraSalariesSmartFastEntryList";
import EmploymentTemplateFormView from "./EmploymentTemplateFormView";

export const workingPatternWorkloadPath = BindingContext.localContext("WorkingPatternWorkload");
export const adjustedSalaryAmountPath = BindingContext.localContext("AdjustedSalaryAmount");

function areExtraSalariesToggled({ storage }: IGetValueArgs): boolean {
    return storage.getValueByPath(ExtraSalaryTogglePath);
}

export const isCompensationType = (args: IGetValueArgs): boolean => {
    // employment template does not have TypeCode right now, so always return false
    if (args.storage.isValidPath(PrEmploymentEntity.TypeCode)) {
        const typeCode = args.storage.getValueByPath(PrEmploymentEntity.TypeCode);
        return typeCode === PrEmploymentTypeCode.Compensation;
    }
    return false;
};

export function getRetirementSavingsTemporalDialogSetting(): IFieldSettings {
    return {
        temporalDialog: {
            dialogTitle: i18next.t("Employment:Form.PlanRetirementSavingsTitle"),
            columns: [
                PrEmploymentRetirementSavingTemporalEntity.ComputationType,
                PrEmploymentRetirementSavingTemporalEntity.IsIncludedAutomatically,
                PrEmploymentRetirementSavingTemporalEntity.Amount,
                PrEmploymentRetirementSavingTemporalEntity.Percentage,
                PrEmploymentRetirementSavingTemporalEntity.SymbolVariable,
                PrEmploymentRetirementSavingTemporalEntity.SymbolSpecific,
                PrEmploymentRetirementSavingTemporalEntity.SymbolConstant,
                PrEmploymentRetirementSavingTemporalEntity.RemittanceInformation
            ],
            isLineItemWithoutValidityProps: true
        }
    };
}

export const getCommonEmploymentDefs = (isTemplate: boolean): TFieldsDefinition => {

    const tmpFieldsDef = { ...TemporalValidityFieldsDef };

    const TemporalEntity = isTemplate ? PrEmploymentTemplateTemporalEntity : PrEmploymentTemporalEntity;
    const MainEntity = isTemplate ? PrEmploymentTemplateEntity : PrEmploymentEntity;

    const isPartialWorkingPattern = (args: IGetValueArgs): boolean => {
        const workingPattern = args.storage.getValueByPath(
            createPath(TemporalEntityProp.CurrentTemporalPropertyBag, TemporalEntity.WorkingPattern)
        ) as Partial<IPrWorkingPatternEntity>;
        const hoursPerWeekFullTime = workingPattern?.HoursPerWeekFullTime ?? DEFAULT_FULL_TIME_HOURS_PER_WEEK;
        const weeklyHours = getWeeklyWorkingHours(workingPattern);
        return !isObjectEmpty(workingPattern) && weeklyHours < hoursPerWeekFullTime;
    };


    return {
        ...getSalaryComponentFieldDefinitions(isTemplate ? SalaryComponentVariant.EmploymentTemplate : SalaryComponentVariant.Employment, {
            moveAmountOpeningButtonToAdjustedAmount: true,
            adjustedAmountFieldId: createPath(MainEntity.SalaryComponents, adjustedSalaryAmountPath)
        }),
        ...getExtraSalaryFieldDefinitions(isTemplate ? ExtraSalaryVariant.EmploymentTemplate : ExtraSalaryVariant.Employment),
        ...getBenefitFieldDefinitions(isTemplate ? BenefitVariant.EmploymentTemplate : BenefitVariant.Employment),
        ...getTemporalPropertyFieldDefinition({
            propName: TemporalEntity.LeaveDays,
            definition: {
                ...getLeaveDaysDef({
                    dialogTitle: i18next.t("Employment:TemplateForm.PlanLeaveDaysChange"),
                    extractHistory: getExtractHistoryFromSettingsFn(TemporalEntity.LeaveDays),
                }),
                additionalProperties: [{
                    id: `/${createPath(PrEmploymentTemplateEntity.TemporalPropertyBag, PrEmploymentTemplateTemporalEntity.WorkingPattern, PrWorkingPatternEntity.TypeCode)}`
                }, {
                    id: `/${createPath(PrEmploymentTemplateEntity.TemporalPropertyBag, PrEmploymentTemplateTemporalEntity.WorkingPattern, PrWorkingPatternEntity.Days, PrWorkingPatternDayEntity.WorkingHours)}`
                }, {
                    id: `/${createPath(PrEmploymentTemplateEntity.CurrentTemporalPropertyBag, PrEmploymentTemplateTemporalEntity.WorkingPattern, PrWorkingPatternEntity.TypeCode)}`
                }, {
                    id: `/${createPath(PrEmploymentTemplateEntity.CurrentTemporalPropertyBag, PrEmploymentTemplateTemporalEntity.WorkingPattern, PrWorkingPatternEntity.Days, PrWorkingPatternDayEntity.WorkingHours)}`
                }, {
                    id: `/${createPath(PrEmploymentTemplateEntity.CurrentTemporalPropertyBag, PrEmploymentTemplateTemporalEntity.WorkingPattern, PrWorkingPatternEntity.WorkingMinutesWeekly)}`
                }]
            },
            isSynchronized: true
        }),
        ...getTemporalPropertyFieldDefinition({
            propName: TemporalEntity.SickDays,
            definition: {
                ...getSickDaysDef({
                    dialogTitle: i18next.t("Employment:TemplateForm.PlanSickDaysChange"),
                    extractHistory: getExtractHistoryFromSettingsFn(TemporalEntity.SickDays),
                }),
                isVisible: args => {
                    const entity = args.storage.data.entity as IPrEmploymentEntity;
                    const typeCode = entity.TypeCode;
                    return PrEmploymentTypeCode.WorkAgreementDPP !== typeCode;
                }
            },
            isSynchronized: true
        }),
        ...getTemporalPropertyFieldDefinition({
            propName: TemporalEntity.WorkingPattern,
            definition: {
                label: i18next.t("Employment:Form.WorkingPattern"),
                type: FieldType.ComboBox,
                width: BasicInputSizes.XL,
                isRequired: ifAny(isTemplate, not(isCompensationType)),
                filter: {
                    select: `${PrWorkingPatternEntity.TypeCode} ne '${PrWorkingPatternTypeCode.Rotation}'`
                },
                fieldSettings: {
                    displayName: "Name",
                    shouldDisplayAdditionalColumns: true,
                    preloadItems: true,
                    temporalDialog: {
                        dialogTitle: i18next.t("Employment:TemplateForm.PlanWorkingPatternChange"),
                        withoutGaps: true,
                        cannotBeEnded: true,
                        granularity: "month",
                        decisiveDateForHistoryValues: args => (args.storage.getCustomData() as IEmploymentCustomData).lastClosedAttendanceDate,
                        dateBeforeDecisiveDateMsg: i18next.t("Employment:TemplateForm.PlanWorkingPatternChangeBeforeLastClosedAttendanceDate"),
                    },
                    additionalProperties: [
                        { id: PrWorkingPatternEntity.TypeCode },
                        { id: createPath(PrWorkingPatternEntity.Days, PrWorkingPatternDayEntity.WorkingHours) },
                        { id: PrWorkingPatternEntity.IsDifferentOddAndEvenWeek },
                        { id: PrWorkingPatternEntity.HoursPerWeekFullTime },
                        { id: PrWorkingPatternEntity.WorkingMinutesWeekly }
                    ],
                    localDependentFields: [
                        {
                            from: { id: PrWorkingPatternEntity.Days }, to: { id: PrWorkingPatternEntity.Days },
                            navigateFrom: NavigationSource.Itself
                        },
                        {
                            from: { id: PrWorkingPatternEntity.TypeCode }, to: { id: PrWorkingPatternEntity.TypeCode },
                            navigateFrom: NavigationSource.Itself
                        },
                        {
                            from: { id: PrWorkingPatternEntity.HoursPerWeekFullTime },
                            to: { id: PrWorkingPatternEntity.HoursPerWeekFullTime },
                            navigateFrom: NavigationSource.Itself
                        },
                        {
                            from: { id: PrWorkingPatternEntity.WorkingMinutesWeekly },
                            to: { id: PrWorkingPatternEntity.WorkingMinutesWeekly },
                            navigateFrom: NavigationSource.Itself
                        },
                    ]
                },
                columns: [
                    { id: "Name" },
                    { id: "Type/Name" }
                ],
                additionalProperties: [
                    { id: PrWorkingPatternEntity.TypeCode },
                    { id: createPath(PrWorkingPatternEntity.Days, PrWorkingPatternDayEntity.WorkingHours) },
                    { id: PrWorkingPatternEntity.IsDifferentOddAndEvenWeek },
                    { id: PrWorkingPatternEntity.HoursPerWeekFullTime },
                    { id: PrWorkingPatternEntity.WorkingMinutesWeekly }
                ]
            },
            isSynchronized: !isTemplate
        }),

        [workingPatternWorkloadPath]: {
            type: FieldType.Custom,
            width: BasicInputSizes.S,
            isVisible: (args) => isPartialWorkingPattern(args),
            render: ({ storage }: IFieldDefFn) => {
                const workingPattern = storage.getValueByPath(
                    createPath("CurrentTemporalPropertyBag", TemporalEntity.WorkingPattern)
                ) as Partial<IPrWorkingPatternEntity>;
                const weeklyHours = getWeeklyWorkingHours(workingPattern);
                const hoursPerWeekFullTime = workingPattern?.HoursPerWeekFullTime ?? DEFAULT_FULL_TIME_HOURS_PER_WEEK;
                if (weeklyHours === null) {
                    return null;
                }
                return (
                    <Text>
                        {i18next.t("Employment:TemplateForm.WorkloadPercentage")}&nbsp;
                        <strong>{roundToDecimalPlaces(2, (weeklyHours / hoursPerWeekFullTime) * 100)} %</strong>
                    </Text>
                );
            }
        },

        [createPath(MainEntity.SalaryComponents, adjustedSalaryAmountPath)]: {
            isDisabled: true,
            type: FieldType.Input,
            label: i18next.t("Employment:TemplateForm.AdjustedSalaryAmount"),
            width: BasicInputSizes.S,
            textAlign: TextAlign.Right,
            fieldSettings: {
                unit: getPayrollCurrencyUnit()
            },
            isRequired: false,
            isVisible: (args) => {
                if (!isPartialWorkingPattern(args)) return false;
                const lineItemBc = args.bindingContext.getParent();
                const salaryComponent = args.storage.getValue(lineItemBc) as IPrSalaryComponentEntity;
                const regularSalaryTypeCode = salaryComponent?.RegularSalaryType?.Code;
                return !!regularSalaryTypeCode && regularSalaryTypeCode !== PrRegularSalaryTypeCode.Hourly;
            },
            extraFieldContentAfter: (args) => getTemporalPropertyExtraFieldContentAfter(undefined, "Amount")(args),
            formatter: (_val, args) => {
                const { bindingContext, storage } = args;
                const workingPattern = storage.getValueByPath(
                    createPath("CurrentTemporalPropertyBag", TemporalEntity.WorkingPattern)
                ) as Partial<IPrWorkingPatternEntity>;
                const weeklyHours = getWeeklyWorkingHours(workingPattern);
                const hoursPerWeekFullTime = workingPattern?.HoursPerWeekFullTime ?? DEFAULT_FULL_TIME_HOURS_PER_WEEK;
                if (weeklyHours === null || weeklyHours >= hoursPerWeekFullTime) return null;
                const lineItemBc = bindingContext.getParent();
                const amountBc = lineItemBc.navigate(
                    createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrEmploymentSalaryComponentTemporalCurrentEntity.Amount)
                );
                const amount = storage.getValue(amountBc) as number;
                if (amount == null) return null;
                const workloadRatio = weeklyHours / hoursPerWeekFullTime;
                return getPayrollCurrencyFormatter()(Math.round(amount * workloadRatio), args);

            }
        },
        [LegislativeExtraSalaryLocalPath]: {
            type: FieldType.Custom,
            label: i18next.t("Employment:TemplateForm.LegislativeExtraSalaries"),
            labelStatus: LabelStatus.Removed,
            render: ({ props }: IFieldDefFn) => {
                return (
                    <LegislativeExtraSalariesSmartFastEntryList {...props}
                                                                groupDefinitionId={ExtraSalaryGrpId}
                                                                isCollapsible/>
                );
            },
            customizationData: {
                useForCustomization: false
            }
        },
        [ExtraSalaryTogglePath]: {
            type: FieldType.Custom,
            label: i18next.t("Employment:TemplateForm.OwnExtraSalaries"),
            render: ({ props }: IFieldDefFn) => (
                <CollapsibleSectionToggleField {...props} />
            ),
            customizationData: {
                useForCustomization: false
            }
        },
        ...tmpFieldsDef
    };
};

export const getCommonEmploymentGroupDef = (isTemplate: boolean): IFormGroupDef[] => {

    const TemporalEntity = isTemplate ? PrEmploymentTemplateTemporalEntity : PrEmploymentTemporalEntity;
    const MainEntity = isTemplate ? PrEmploymentTemplateEntity : PrEmploymentEntity;
    const SalaryComponentEntity = isTemplate ? PrEmploymentTemplateSalaryComponentEntity : PrEmploymentSalaryComponentEntity;
    const BenefitEntity = isTemplate ? PrEmploymentTemplateBenefitEntity : PrEmploymentBenefitEntity;
    const ExtraSalaryEntity = isTemplate ? PrEmploymentTemplateExtraSalaryEntity : PrEmploymentExtraSalaryEntity;
    const ExtraSalaryTemporalCurrentEntity = isTemplate ? PrEmploymentTemplateExtraSalaryTemporalCurrentEntity : PrEmploymentExtraSalaryTemporalCurrentEntity;

    const salaryType = isTemplate ? SalaryComponentVariant.EmploymentTemplate : SalaryComponentVariant.Employment;
    const benefitType = isTemplate ? BenefitVariant.EmploymentTemplate : BenefitVariant.Employment;

    const salaryColumns = getTemporalDataColumns(getSalaryComponentColumns(salaryType), false)
        .map(id => ({ id: createPath(SalaryComponentEntity.CurrentTemporalPropertyBag, id) }));

    const benefitColumns = getTemporalDataColumns(getBenefitColumns(benefitType), false)
        .map(id => ({ id: createPath(PrBenefitEntity.CurrentTemporalPropertyBag, id) }));

    return [{
        id: "workingTime",
        title: i18next.t("Employment:TemplateForm.WorkingTime"),
        isVisible: not(isCompensationType),
        rows: [
            [{ id: createPath(MainEntity.CurrentTemporalPropertyBag, TemporalEntity.WorkingPattern) }],
            [{ id: workingPatternWorkloadPath }]
        ]
    }, {
        id: "salaryTitle",
        title: i18next.t("Employment:TemplateForm.SalaryMainTitle"),
        isVisible: true
    }, {
        id: SalaryComponentGrpId,
        title: i18next.t("Employment:TemplateForm.SalaryComponents"),
        showGroupDivider: false,
        lineItems: {
            collection: MainEntity.SalaryComponents,
            order: "Id",
            useLabelWrapping: areAllSalaryComponentsOfSameType,
            disableVirtualization: true,
            temporalDialog: getSalaryComponentTemporalDialogSettings(salaryType),
            columns: [
                salaryColumns[0], // Name
                { id: SalaryComponentEntity.Type },
                { id: SalaryComponentEntity.RegularSalaryType },
                ...salaryColumns.slice(1), // other columns
                { id: adjustedSalaryAmountPath }
            ],
            canReorder: false,
            isItemCloneable: false,
            isAddButtonTransparent: true,
            isTheOnlyItemRemovable: true,
            customActionButtons: [{
                id: DialogType.SalaryComponent,
                title: i18next.t("Employment:TemplateForm.Pick"),
                position: ActionButtonPosition.Before,
                isVisible: not(isFormReadOnly),
                isDisabled: isFormDisabled,
                isTransparent: false
            }]
        }
    }, {
        id: BenefitGrpId,
        title: i18next.t("Employment:TemplateForm.Benefits"),
        showGroupDivider: false,
        lineItems: {
            collection: MainEntity.Benefits,
            order: "Id",
            useLabelWrapping: areAllBenefitsOfSameType,
            disableVirtualization: true,
            temporalDialog: getBenefitTemporalDialogSettings(benefitType),
            columns: [
                benefitColumns[0], // Name
                { id: BenefitEntity.Type },
                ...benefitColumns.slice(1) // other columns
            ],
            additionalFields: [
                { id: BenefitEntity.Type },
            ],
            canReorder: false,
            isItemCloneable: false,
            isAddButtonTransparent: true,
            isTheOnlyItemRemovable: true,
            customActionButtons: [{
                id: DialogType.Benefit,
                title: i18next.t("Employment:TemplateForm.Pick"),
                position: ActionButtonPosition.Before,
                isVisible: not(isFormReadOnly),
                isDisabled: isFormDisabled,
                isTransparent: false
            }]
        }
    }, {
        id: ExtraSalaryGrpId,
        title: i18next.t("Employment:TemplateForm.ExtraSalaries"),
        showGroupDivider: false,
        rows: [
            [{ id: LegislativeExtraSalaryLocalPath }],
            [{ id: ExtraSalaryTogglePath }]
        ],
        isVisible: not(isCompensationType),
        lineItems: {
            collection: MainEntity.ExtraSalaries,
            order: "Id",
            temporalDialog: getExtraSalaryTemporalDialogSettings(),
            columns: [
                { id: createPath(ExtraSalaryEntity.CurrentTemporalPropertyBag, ExtraSalaryTemporalCurrentEntity.Name) },
                { id: createPath(ExtraSalaryEntity.CurrentTemporalPropertyBag, ExtraSalaryTemporalCurrentEntity.ComputationType) },
                { id: createPath(ExtraSalaryEntity.CurrentTemporalPropertyBag, ExtraSalaryTemporalCurrentEntity.Value) }
            ],
            additionalFields: [
                { id: createPath(ExtraSalaryEntity.Default, PrExtraSalaryEntity.Type, PrExtraSalaryTypeEntity.OriginCode) }
            ],
            canAdd: areExtraSalariesToggled,
            canReorder: false,
            isItemCloneable: false,
            // LegislativeExtraSalaries are rendered separately, so they can be collapsed
            isItemVisible: ifAll(not(isLegislativeExtraSalary), areExtraSalariesToggled),
            isAddButtonTransparent: true,
            isTheOnlyItemRemovable: true,
            customActionButtons: [{
                id: DialogType.ExtraSalary,
                title: i18next.t("Employment:TemplateForm.Pick"),
                position: ActionButtonPosition.Before,
                isTransparent: false,
                isVisible: ifAll(not(isFormReadOnly), areExtraSalariesToggled),
                isDisabled: isFormDisabled,
            }]
        }
    }, ...(isTemplate ? [] : [{
        id: "cars",
        title: i18next.t("Employment:Form.Cars"),
        lineItems: {
            collection: PrEmploymentEntity.Cars,
            order: "Id",
            canReorder: false,
            isItemCloneable: false,
            isGroupToggleable: true,
            temporalDialog: null,
            columns: [
                { id: PrEmploymentCarEntity.Name },
                { id: PrEmploymentCarEntity.PurchasePrice },
                { id: PrEmploymentCarEntity.Taxability },
                { id: PrEmploymentCarEntity.DateValidFrom },
                { id: PrEmploymentCarEntity.DateValidTo }
            ]
        }
    }, {
        id: "RetirementSavings",
        title: i18next.t("Employment:Form.RetirementSavings"),
        lineItems: {
            collection: PrEmploymentEntity.RetirementSavings,
            order: "Id",
            canReorder: false,
            isItemCloneable: false,
            useLabelWrapping: areAllRetirementsOfSameType.bind(this, "CurrentTemporalPropertyBag/ComputationTypeCode"),
            isGroupToggleable: true,
            isTheOnlyItemRemovable: true,
            additionalFields: [{ id: createPath(PrEmploymentRetirementSavingEntity.RetirementSavingsProduct, PrRetirementSavingsProductEntity.PrRetirementSavingsCompany, PrRetirementSavingsCompanyEntity.Name) },
                { id: createPath(PrEmploymentRetirementSavingEntity.RetirementSavingsProduct, PrRetirementSavingsProductEntity.PrRetirementSavingsCompany, PrRetirementSavingsCompanyEntity.Id) }],
            ...getRetirementSavingsTemporalDialogSetting(),
            columns: [
                { id: createPath(PrEmploymentRetirementSavingEntity.RetirementSavingsProduct, PrRetirementSavingsProductEntity.Type) },
                { id: productPath },
                ...([
                    PrEmploymentRetirementSavingTemporalCurrentEntity.ComputationType,
                    PrEmploymentRetirementSavingTemporalCurrentEntity.IsIncludedAutomatically,
                    PrEmploymentRetirementSavingTemporalCurrentEntity.Amount,
                    PrEmploymentRetirementSavingTemporalCurrentEntity.Percentage,
                    PrEmploymentRetirementSavingTemporalCurrentEntity.SymbolVariable,
                    PrEmploymentRetirementSavingTemporalCurrentEntity.SymbolSpecific,
                    PrEmploymentRetirementSavingTemporalCurrentEntity.SymbolConstant,
                    PrEmploymentRetirementSavingTemporalCurrentEntity.RemittanceInformation,
                ].map(id => ({ id: createPath(PrEmploymentRetirementSavingEntity.CurrentTemporalPropertyBag, id) })))
            ]
        }
    }]), {
        id: "absence",
        title: i18next.t("Employment:TemplateForm.AbsenceValues"),
        isVisible: not(isCompensationType),

        rows: [[
            { id: createPath(MainEntity.CurrentTemporalPropertyBag, TemporalEntity.LeaveDays) },
            { id: createPath(MainEntity.CurrentTemporalPropertyBag, TemporalEntity.SickDays) }
        ]]
    }
    ];
};

const hasOwnExtraSalariesPath = BindingContext.localContext("hasOwnExtraSalaries");
const monthAmountSumPath = BindingContext.localContext("monthAmountSum");
const hourAmountSumPath = BindingContext.localContext("hourAmountSum");

const tableAmountFormatter = (typeCode: PrSalaryComponentTypeCode) => {
    return (val: TValue, args: IFormatOptions) => {
        const value = (args.entity as IPrEmploymentTemplateEntity).SalaryComponents?.reduce((acc, curr) => {
            if (curr?.TypeCode === typeCode) {
                acc += curr.CurrentTemporalPropertyBag.Amount;
            }
            return acc;
        }, 0);
        return formatCurrency(value ?? 0, getCompanyCurrency(args.context));
    };
};

export const getDefinitions: IGetDefinition = (): IDefinition => {
    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.PrEmploymentTemplate}Table`,
        additionalProperties: [{
            id: createPath(PrEmploymentTemplateEntity.ExtraSalaries, PrEmploymentTemplateExtraSalaryEntity.Default, PrExtraSalaryEntity.TypeCode)
        }, {
            id: createPath(PrEmploymentTemplateEntity.SalaryComponents, PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrEmploymentSalaryComponentTemporalCurrentEntity.Amount)
        }, {
            id: createPath(PrEmploymentTemplateEntity.SalaryComponents, PrSalaryComponentEntity.TypeCode)
        }],
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            defaultFilters: [
                PrEmploymentTemplateEntity.NumberOurs,
                PrEmploymentTemplateEntity.Name,
                createPath(PrEmploymentTemplateEntity.CurrentTemporalPropertyBag, PrEmploymentTemplateTemporalEntity.WorkingPattern)
            ],
            filterDefinition: {
                [PrEmploymentTemplateEntity.NumberOurs]: {},
                [PrEmploymentTemplateEntity.Name]: {},
                [createPath(PrEmploymentTemplateEntity.CurrentTemporalPropertyBag, PrEmploymentTemplateTemporalEntity.WorkingPattern)]: {
                    fieldSettings: {
                        displayName: PrWorkingPatternEntity.Name
                    }
                }
            },
            isValueHelp: true
        }],
        initialSortBy: [{ id: PrEmploymentTemplateEntity.NumberOurs, sort: Sort.Asc }],
        columns: [
            PrEmploymentTemplateEntity.NumberOurs,
            PrEmploymentTemplateEntity.Name,
            createPath(PrEmploymentTemplateEntity.CurrentTemporalPropertyBag, PrEmploymentTemplateTemporalEntity.WorkingPattern),
            hasOwnExtraSalariesPath,
            monthAmountSumPath,
            hourAmountSumPath
        ],
        columnDefinition: {
            [PrEmploymentTemplateEntity.NumberOurs]: {},
            [PrEmploymentTemplateEntity.Name]: {},
            [createPath(PrEmploymentTemplateEntity.CurrentTemporalPropertyBag, PrEmploymentTemplateTemporalEntity.WorkingPattern)]: {
                fieldSettings: {
                    displayName: PrWorkingPatternEntity.Name
                }
            },
            [hasOwnExtraSalariesPath]: {
                label: i18next.t("Employment:TemplateTable.OwnExtraSalaries"),
                formatter: (val: TValue, args: IFormatOptions) => {
                    const hasOwnExtraSalary = (args.entity as IPrEmploymentTemplateEntity).ExtraSalaries?.some(salary => !isLegislativeExtraSalaryItem(salary)) ?? false;
                    return i18next.t(`Common:General.${hasOwnExtraSalary ? "Yes" : "No"}`).toString();
                }
            },
            [monthAmountSumPath]: {
                label: i18next.t("Employment:TemplateTable.MonthAmountSum"),
                formatter: tableAmountFormatter(PrSalaryComponentTypeCode.Regular)
            },
            [hourAmountSumPath]: {
                label: i18next.t("Employment:TemplateTable.HourAmountSum"),
                formatter: tableAmountFormatter(PrSalaryComponentTypeCode.Regular)
            }
        },
        title: i18next.t("Employment:Template.Title")
    };

    const summary: ISummaryItem[] = [{
        ...getNumberOursSummaryDef("Employment")
    }];
    const form: IFormDef = {
        id: `${EntityTypeName.PrEmploymentTemplate}Form`,
        translationFiles: getDefinitions.translationFiles,
        getItemBreadCrumbText: (storage: Model) =>
            getItemBreadCrumbsText(storage, i18next.t("Employment:Template.New"), i18next.t("Employment:Template.FormTitle")),
        isDeletable: true,
        formControl: EmploymentTemplateFormView,
        summary,
        additionalProperties: [
            ...getTemporalAdditionalProperties(PrEmploymentTemplateTemporalEntity, PrEmploymentTemplateEntity),
            ...NumberRangeAdditionalProperties,
            {
                id: PrEmploymentTemplateEntity.ExtraSalaries,
                additionalProperties: [
                    {
                        id: PrEmploymentTemplateExtraSalaryEntity.Default,
                        additionalProperties: [
                            { id: PrExtraSalaryEntity.TypeCode },
                            {
                                id: PrExtraSalaryEntity.Type,
                                additionalProperties: [{
                                    id: PrExtraSalaryTypeEntity.OriginCode
                                }]
                            }
                        ]
                    }
                ]
            },
            {
                id: PrEmploymentTemplateEntity.SalaryComponents,
                additionalProperties: [
                    { id: PrEmploymentTemplateSalaryComponentEntity.Default }
                ]
            }
        ],
        fieldDefinition: {
            ...getNumberRangeFieldDefs("Employment"),
            [PrEmploymentTemplateEntity.Name]: {
                width: BasicInputSizes.XL
            },
            ...getCommonEmploymentDefs(true)
        },
        groups: [
            {
                id: "main",
                rows: [[
                    { id: PrEmploymentTemplateEntity.Name }
                ]]
            },
            ...getCommonEmploymentGroupDef(true)
        ]
    };
    return {
        entitySet: EntitySetName.PrEmploymentTemplates,
        table,
        form
    };
};
getDefinitions.translationFiles = [...EmploymentTranslationFiles];
setDefByEntityType(EntityTypeName.PrEmploymentTemplate, getDefinitions);
